import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import axios from 'axios';
import API_BASE from '../../utils/api';

const API = API_BASE;

const DESIGNATION_SUGGESTIONS = ['Librarian', 'Accountant', 'Driver', 'Peon', 'Security Guard', 'Clerk', 'Lab Assistant', 'Receptionist'];
const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' }, { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const emptyStaffForm = { staffId: '', name: '', designation: '', phone: '', address: '', qualification: '', joiningDate: '' };
const emptySalaryForm = {
  month: new Date().getMonth() + 1, year: new Date().getFullYear(), salaryMode: 'attendance',
  fullMonthPay: '', totalWorkingDays: '', daysPresent: '', daysAbsent: '',
  hra: '', da: '', allowances: '', deductions: '', tax: '', performanceRating: '', performanceBonus: '', remarks: '',
};

const PrincipalNonTeachingStaff = () => {
  const [activeTab, setActiveTab] = useState('directory');
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState(emptyStaffForm);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [salaries, setSalaries] = useState([]);
  const [salaryForm, setSalaryForm] = useState(emptySalaryForm);
  const [savingSalary, setSavingSalary] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/non-teaching-staff`, { headers, params: { search } });
      setStaffList(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const fetchSalaries = useCallback(async (staffId) => {
    if (!staffId) { setSalaries([]); return; }
    try {
      const res = await axios.get(`${API}/non-teaching-staff/${staffId}/salary`, { headers });
      setSalaries(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { if (activeTab === 'salary') fetchSalaries(selectedStaffId); }, [activeTab, selectedStaffId, fetchSalaries]);

  const openCreateModal = async () => {
    setEditingStaff(null);
    setStaffForm({ ...emptyStaffForm, staffId: 'Generating…' });
    setShowModal(true);
    try {
      const res = await axios.get(`${API}/non-teaching-staff/next-staff-id`, { headers });
      setStaffForm((f) => ({ ...f, staffId: res.data.staffId }));
    } catch {
      setStaffForm((f) => ({ ...f, staffId: '' }));
    }
  };

  const openEditModal = (staff) => {
    setEditingStaff(staff);
    setStaffForm({
      staffId: staff.staffId, name: staff.name, designation: staff.designation, phone: staff.phone,
      address: staff.address || '', qualification: staff.qualification || '',
      joiningDate: staff.joiningDate ? staff.joiningDate.split('T')[0] : '',
    });
    setShowModal(true);
  };

  const handleSaveStaff = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        await axios.put(`${API}/non-teaching-staff/${editingStaff._id}`, staffForm, { headers });
        showMessage('success', 'Staff member updated');
      } else {
        await axios.post(`${API}/non-teaching-staff`, staffForm, { headers });
        showMessage('success', 'Staff member added');
      }
      setShowModal(false);
      fetchStaff();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to save staff member');
    }
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await axios.delete(`${API}/non-teaching-staff/${id}`, { headers });
      showMessage('success', 'Staff member deleted');
      fetchStaff();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to delete');
    }
  };

  const calculateNet = () => {
    const full = parseFloat(salaryForm.fullMonthPay) || 0;
    let basic = full;
    if (salaryForm.salaryMode === 'attendance' && salaryForm.totalWorkingDays) {
      basic = Math.round((full / parseFloat(salaryForm.totalWorkingDays)) * (parseFloat(salaryForm.daysPresent) || 0));
    }
    const hra = parseFloat(salaryForm.hra) || 0, da = parseFloat(salaryForm.da) || 0;
    const allow = parseFloat(salaryForm.allowances) || 0, deduct = parseFloat(salaryForm.deductions) || 0, tax = parseFloat(salaryForm.tax) || 0;
    const bonus = salaryForm.salaryMode === 'performance' ? (parseFloat(salaryForm.performanceBonus) || 0) : 0;
    return basic + hra + da + allow + bonus - deduct - tax;
  };

  const handleGenerateSalary = async (e) => {
    e.preventDefault();
    if (!selectedStaffId || !salaryForm.fullMonthPay) {
      showMessage('error', 'Select a staff member and enter full month pay');
      return;
    }
    try {
      setSavingSalary(true);
      await axios.post(`${API}/non-teaching-staff/${selectedStaffId}/salary/generate`, {
        ...salaryForm,
        fullMonthPay: parseFloat(salaryForm.fullMonthPay),
        totalWorkingDays: parseFloat(salaryForm.totalWorkingDays) || 0,
        daysPresent: parseFloat(salaryForm.daysPresent) || 0,
        daysAbsent: parseFloat(salaryForm.daysAbsent) || 0,
        // These come straight from text inputs as strings — the spread above
        // would otherwise send them unparsed, which made the backend's
        // netPay addition concatenate digits instead of summing them.
        hra: parseFloat(salaryForm.hra) || 0,
        da: parseFloat(salaryForm.da) || 0,
        allowances: parseFloat(salaryForm.allowances) || 0,
        deductions: parseFloat(salaryForm.deductions) || 0,
        tax: parseFloat(salaryForm.tax) || 0,
        performanceBonus: parseFloat(salaryForm.performanceBonus) || 0,
      }, { headers });
      showMessage('success', 'Salary generated successfully!');
      setSalaryForm(emptySalaryForm);
      fetchSalaries(selectedStaffId);
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to generate salary');
    } finally {
      setSavingSalary(false);
    }
  };

  const handleMarkPaid = async (salaryId, paymentMode) => {
    try {
      await axios.put(`${API}/non-teaching-staff/salary/${salaryId}/pay`, { paymentMode }, { headers });
      showMessage('success', 'Marked as paid!');
      fetchSalaries(selectedStaffId);
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to mark as paid');
    }
  };

  const handleDeleteSalary = async (salaryId) => {
    if (!window.confirm('Delete this salary record?')) return;
    try {
      await axios.delete(`${API}/non-teaching-staff/salary/${salaryId}`, { headers });
      showMessage('success', 'Salary record deleted');
      fetchSalaries(selectedStaffId);
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to delete');
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">🧑‍🔧 Non-Teaching Staff</h1>
              <p className="text-gray-500 mt-1">Directory and salary for librarians, drivers, clerks, and other support staff</p>
            </div>
            <ProfileDropdown />
          </div>

          {message.text && (
            <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
              {message.text}
            </div>
          )}

          <div className="flex space-x-1 bg-gray-200 rounded-lg p-1 mb-6 w-fit">
            <button onClick={() => setActiveTab('directory')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'directory' ? 'bg-white text-purple-700 shadow' : 'text-gray-600 hover:text-gray-800'}`}>
              Staff Directory
            </button>
            <button onClick={() => setActiveTab('salary')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'salary' ? 'bg-white text-purple-700 shadow' : 'text-gray-600 hover:text-gray-800'}`}>
              Salary
            </button>
          </div>

          {activeTab === 'directory' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, ID, or designation..."
                  className="border border-gray-300 rounded-lg px-3 py-2 w-72 focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                <button onClick={openCreateModal} className="bg-purple-600 text-white px-5 py-2 rounded-lg hover:bg-purple-700 transition-colors font-medium">
                  + Add Staff
                </button>
              </div>

              {loading ? (
                <div className="text-center py-10 text-gray-500">Loading...</div>
              ) : staffList.length === 0 ? (
                <div className="text-center py-10 text-gray-400">No staff members found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="p-3 text-sm font-semibold text-gray-600">Staff ID</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Name</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Designation</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Phone</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Status</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staffList.map((s) => (
                        <tr key={s._id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-sm font-mono">{s.staffId}</td>
                          <td className="p-3 text-sm font-medium">{s.name}</td>
                          <td className="p-3 text-sm">{s.designation}</td>
                          <td className="p-3 text-sm">{s.phone}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {s.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button onClick={() => openEditModal(s)} className="text-purple-600 hover:text-purple-800 text-xs px-2 py-1 border border-purple-300 rounded hover:bg-purple-50">Edit</button>
                              <button onClick={() => handleDeleteStaff(s._id)} className="text-red-500 hover:text-red-700 text-xs px-2 py-1 border border-red-300 rounded hover:bg-red-50">Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'salary' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-6">Generate Pay Slip</h2>
                <form onSubmit={handleGenerateSalary}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member *</label>
                      <select value={selectedStaffId} onChange={(e) => setSelectedStaffId(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2" required>
                        <option value="">Select Staff</option>
                        {staffList.map((s) => <option key={s._id} value={s._id}>{s.name} ({s.staffId}) — {s.designation}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Month *</label>
                      <select value={salaryForm.month} onChange={(e) => setSalaryForm({ ...salaryForm, month: parseInt(e.target.value) })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2">
                        {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                      <input type="number" value={salaryForm.year} onChange={(e) => setSalaryForm({ ...salaryForm, year: parseInt(e.target.value) })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2" min="2020" max="2099" />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Salary Mode</label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setSalaryForm({ ...salaryForm, salaryMode: 'attendance' })}
                        className={`px-5 py-2 rounded-lg font-medium transition-colors ${salaryForm.salaryMode === 'attendance' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        📋 Attendance Based
                      </button>
                      <button type="button" onClick={() => setSalaryForm({ ...salaryForm, salaryMode: 'performance' })}
                        className={`px-5 py-2 rounded-lg font-medium transition-colors ${salaryForm.salaryMode === 'performance' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        ⭐ Performance Based
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Non-teaching staff have no login/class records, so working &amp; present days are entered manually below.</p>
                  </div>

                  {salaryForm.salaryMode === 'attendance' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Total Working Days</label>
                        <input type="number" value={salaryForm.totalWorkingDays} onChange={(e) => setSalaryForm({ ...salaryForm, totalWorkingDays: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2" min="0" placeholder="0" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Days Present</label>
                        <input type="number" value={salaryForm.daysPresent} onChange={(e) => setSalaryForm({ ...salaryForm, daysPresent: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2" min="0" placeholder="0" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Days Absent</label>
                        <input type="number" value={salaryForm.daysAbsent} onChange={(e) => setSalaryForm({ ...salaryForm, daysAbsent: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2" min="0" placeholder="0" />
                      </div>
                    </div>
                  )}

                  <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">Pay Breakdown</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Month Pay (₹) *</label>
                      <input type="number" value={salaryForm.fullMonthPay} onChange={(e) => setSalaryForm({ ...salaryForm, fullMonthPay: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="0" min="0" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">HRA (₹)</label>
                      <input type="number" value={salaryForm.hra} onChange={(e) => setSalaryForm({ ...salaryForm, hra: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="0" min="0" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">DA (₹)</label>
                      <input type="number" value={salaryForm.da} onChange={(e) => setSalaryForm({ ...salaryForm, da: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="0" min="0" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Other Allowances (₹)</label>
                      <input type="number" value={salaryForm.allowances} onChange={(e) => setSalaryForm({ ...salaryForm, allowances: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="0" min="0" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deductions (₹)</label>
                      <input type="number" value={salaryForm.deductions} onChange={(e) => setSalaryForm({ ...salaryForm, deductions: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="0" min="0" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tax (₹)</label>
                      <input type="number" value={salaryForm.tax} onChange={(e) => setSalaryForm({ ...salaryForm, tax: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="0" min="0" />
                    </div>
                  </div>

                  {salaryForm.salaryMode === 'performance' && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-purple-800 mb-3">⭐ Performance Evaluation</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Rating (1-5)</label>
                          <select value={salaryForm.performanceRating} onChange={(e) => setSalaryForm({ ...salaryForm, performanceRating: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2">
                            <option value="">Select Rating</option>
                            {[1, 2, 3, 4, 5].map((r) => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Performance Bonus (₹)</label>
                          <input type="number" value={salaryForm.performanceBonus} onChange={(e) => setSalaryForm({ ...salaryForm, performanceBonus: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="0" min="0" />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg p-4 mb-6 flex justify-between items-center">
                    <span className="text-lg font-medium">Net Pay</span>
                    <span className="text-3xl font-bold">₹{calculateNet().toLocaleString('en-IN')}</span>
                  </div>

                  <button type="submit" disabled={savingSalary}
                    className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 font-medium">
                    {savingSalary ? 'Generating...' : 'Generate Salary'}
                  </button>
                </form>
              </div>

              {selectedStaffId && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Salary Records</h3>
                  {salaries.length === 0 ? (
                    <div className="text-center py-6 text-gray-400">No salary records yet</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="bg-gray-50 border-b">
                            <th className="p-3 text-sm font-semibold text-gray-600">Month/Year</th>
                            <th className="p-3 text-sm font-semibold text-gray-600">Net Pay</th>
                            <th className="p-3 text-sm font-semibold text-gray-600">Status</th>
                            <th className="p-3 text-sm font-semibold text-gray-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salaries.map((s) => (
                            <tr key={s._id} className="border-b hover:bg-gray-50">
                              <td className="p-3 text-sm">{months.find((m) => m.value === s.month)?.label} {s.year}</td>
                              <td className="p-3 font-bold text-green-700">₹{s.netPay?.toLocaleString('en-IN')}</td>
                              <td className="p-3">
                                {s.status === 'paid'
                                  ? <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">✅ Paid</span>
                                  : <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">⏳ Generated</span>}
                              </td>
                              <td className="p-3">
                                {s.status === 'generated' && (
                                  <div className="flex gap-2">
                                    <select onChange={(e) => { if (e.target.value) handleMarkPaid(s._id, e.target.value); }}
                                      className="text-xs border rounded px-2 py-1" defaultValue="">
                                      <option value="" disabled>Pay via...</option>
                                      <option value="bank_transfer">Bank Transfer</option>
                                      <option value="cheque">Cheque</option>
                                      <option value="cash">Cash</option>
                                      <option value="upi">UPI</option>
                                    </select>
                                    <button onClick={() => handleDeleteSalary(s._id)}
                                      className="text-red-500 hover:text-red-700 text-xs px-2 py-1 border border-red-300 rounded hover:bg-red-50">🗑️</button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">{editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}</h2>
              <button onClick={() => setShowModal(false)} className="text-white text-2xl hover:text-purple-100">×</button>
            </div>
            <form onSubmit={handleSaveStaff} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff ID</label>
                <input value={staffForm.staffId} readOnly className="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation *</label>
                <input list="designation-suggestions" value={staffForm.designation} onChange={(e) => setStaffForm({ ...staffForm, designation: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2" required />
                <datalist id="designation-suggestions">
                  {DESIGNATION_SUGGESTIONS.map((d) => <option key={d} value={d} />)}
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  className="w-full border rounded-lg px-3 py-2" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Qualification</label>
                <input value={staffForm.qualification} onChange={(e) => setStaffForm({ ...staffForm, qualification: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Joining Date</label>
                <input type="date" value={staffForm.joiningDate} onChange={(e) => setStaffForm({ ...staffForm, joiningDate: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea value={staffForm.address} onChange={(e) => setStaffForm({ ...staffForm, address: e.target.value })}
                  rows="2" className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">{editingStaff ? 'Update' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrincipalNonTeachingStaff;
